import { useState, useEffect, useCallback, useMemo } from "react";
import { supabase, Student, SchoolClass, School, logActivity } from "@/lib/supabase";
import { useAuth } from "@/lib/auth";
import { Users, School as SchoolIcon, UserX, UserCheck, Search, ClipboardCheck } from "lucide-react";

const currentAcademicYear = new Date().getFullYear();
const ACADEMIC_YEAR_OPTIONS = [currentAcademicYear - 1, currentAcademicYear, currentAcademicYear + 1];

type EnrollmentStatus = "active" | "dropped_out" | "graduated" | "transferred";

const STATUS_LABELS: Record<EnrollmentStatus, string> = {
  active: "Active",
  dropped_out: "Dropped Out",
  graduated: "Graduated",
  transferred: "Transferred",
};

const STATUS_COLORS: Record<EnrollmentStatus, string> = {
  active: "#2E7D32",
  dropped_out: "#D32F2F",
  graduated: "#1C3988",
  transferred: "#D97706",
};

interface StudentManagementProps {
  onTakeAttendance?: (target: { classId: string; level: "preschool" | "primary" | "secondary"; shift: "morning" | "afternoon"; schoolId?: string }) => void;
}

export default function StudentManagement({ onTakeAttendance }: StudentManagementProps = {}) {
  const { user } = useAuth();

  const [schools, setSchools] = useState<School[]>([]);
  const [selectedSchoolId, setSelectedSchoolId] = useState("");
  const [academicYear, setAcademicYear] = useState(currentAcademicYear);

  const [students, setStudents] = useState<Student[]>([]);
  const [classes, setClasses] = useState<SchoolClass[]>([]);

  const [levelFilter, setLevelFilter] = useState<"all" | "preschool" | "primary" | "secondary">("all");
  const [shiftFilter, setShiftFilter] = useState<"all" | "morning" | "afternoon">("all");
  const [gradeFilter, setGradeFilter] = useState<string>("all");
  const [sectionFilter, setSectionFilter] = useState<string>("all");
  const [enrollmentFilter, setEnrollmentFilter] = useState<"all" | EnrollmentStatus>("active");
  const [search, setSearch] = useState("");

  const [loading, setLoading] = useState(false);
  const [savingId, setSavingId] = useState<string | null>(null);

  useEffect(() => {
    supabase.from("schools").select("*").order("name").then(({ data }) => {
      if (data) {
        setSchools(data as School[]);
        if (data.length > 0 && !selectedSchoolId) setSelectedSchoolId((data[0] as School).id);
      }
    });
  }, []);

  const fetchClasses = useCallback(async () => {
    if (!selectedSchoolId) return;
    const { data } = await supabase
      .from("classes")
      .select("*")
      .eq("school_id", selectedSchoolId)
      .eq("academic_year", academicYear)
      .order("name");
    if (data) setClasses(data as SchoolClass[]);
  }, [selectedSchoolId, academicYear]);

  const fetchStudents = useCallback(async () => {
    if (!selectedSchoolId) return;
    setLoading(true);
    let q = supabase
      .from("students")
      .select("*")
      .eq("school_id", selectedSchoolId)
      .eq("academic_year", academicYear);

    if (levelFilter !== "all") q = q.eq("level", levelFilter);
    if (shiftFilter !== "all") q = q.eq("shift", shiftFilter);
    if (enrollmentFilter !== "all") q = q.eq("enrollment_status", enrollmentFilter);

    const { data } = await q.order("full_name");
    if (data) setStudents(data as Student[]);
    setLoading(false);
  }, [selectedSchoolId, academicYear, levelFilter, shiftFilter, enrollmentFilter]);

  useEffect(() => { fetchClasses(); }, [fetchClasses]);
  useEffect(() => { fetchStudents(); }, [fetchStudents]);

  useEffect(() => {
    const channel = supabase
      .channel("student-management-realtime")
      .on("postgres_changes", { event: "*", schema: "public", table: "students" }, () => fetchStudents())
      .on("postgres_changes", { event: "*", schema: "public", table: "classes" }, () => fetchClasses())
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [fetchStudents, fetchClasses]);

  const classById = useMemo(() => {
    const map: Record<string, SchoolClass> = {};
    classes.forEach((c) => { map[c.id] = c; });
    return map;
  }, [classes]);

  const gradeOptions = useMemo(
    () => [...new Set(classes.map((c) => (c as any).grade).filter(Boolean))].sort(),
    [classes]
  );
  const sectionOptions = useMemo(
    () => [...new Set(classes.map((c) => (c as any).section).filter(Boolean))].sort(),
    [classes]
  );

  const filteredStudents = students.filter((s) => {
    const cls = s.class_id ? classById[s.class_id] : null;
    if (gradeFilter !== "all" && (cls as any)?.grade !== gradeFilter) return false;
    if (sectionFilter !== "all" && (cls as any)?.section !== sectionFilter) return false;
    if (search && !s.full_name.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  // When Level + Shift + Grade + Section all narrow down to exactly one
  // class, surface a direct "Take Attendance" shortcut for that roster.
  const resolvedClass = useMemo(() => {
    if (levelFilter === "all" || shiftFilter === "all" || gradeFilter === "all") return null;
    if (levelFilter === "primary" && sectionFilter === "all" && gradeFilter !== "ECD") return null;
    return classes.find((c) =>
      c.level === levelFilter &&
      c.shift === shiftFilter &&
      (c as any).grade === gradeFilter &&
      ((c as any).section === (sectionFilter === "all" ? null : sectionFilter) || (gradeFilter === "ECD"))
    ) || null;
  }, [classes, levelFilter, shiftFilter, gradeFilter, sectionFilter]);

  const activeInResolvedClass = resolvedClass
    ? filteredStudents.filter((s) => s.class_id === resolvedClass.id && ((s as any).enrollment_status || "active") === "active").length
    : 0;

  const summary = useMemo(() => {
    const counts: Record<EnrollmentStatus, number> = { active: 0, dropped_out: 0, graduated: 0, transferred: 0 };
    students.forEach((s) => {
      const status = ((s as any).enrollment_status || "active") as EnrollmentStatus;
      if (counts[status] !== undefined) counts[status]++;
    });
    return counts;
  }, [students]);

  const reassignClass = async (studentId: string, newClassId: string) => {
    setSavingId(studentId);
    const cls = classes.find((c) => c.id === newClassId);
    await supabase.from("students").update({ class_id: newClassId || null }).eq("id", studentId);
    const student = students.find((s) => s.id === studentId);
    await logActivity("reassigned class", "student", studentId, student?.full_name, selectedSchoolId, cls ? cls.name : "Unassigned");
    setSavingId(null);
    await fetchStudents();
  };

  const setEnrollmentStatus = async (studentId: string, status: EnrollmentStatus) => {
    setSavingId(studentId);
    await supabase.from("students").update({ enrollment_status: status }).eq("id", studentId);
    const student = students.find((s) => s.id === studentId);
    await logActivity(`marked student ${STATUS_LABELS[status].toLowerCase()}`, "student", studentId, student?.full_name, selectedSchoolId);
    setSavingId(null);
    await fetchStudents();
  };

  return (
    <div className="animate-fadeIn">
      <div className="card mb-5">
        <div className="mb-5 pb-3 border-b-2 border-gray-200">
          <h2 className="text-[#1C3988] flex items-center gap-3 text-xl font-bold">
            <Users className="w-6 h-6" /> Student Management
          </h2>
          <p className="text-gray-500 text-sm mt-1">
            View confirmed students by academic year and class group, reassign classes, and manage dropouts/transfers/graduation.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-5">
          <div className="form-group">
            <label className="form-label">Select School</label>
            <div className="relative">
              <SchoolIcon className="absolute left-3 top-3.5 w-5 h-5 text-gray-400" />
              <select className="form-input pl-10" value={selectedSchoolId} onChange={(e) => setSelectedSchoolId(e.target.value)}>
                {schools.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Academic Year</label>
            <select className="form-input" value={academicYear} onChange={(e) => setAcademicYear(Number(e.target.value))}>
              {ACADEMIC_YEAR_OPTIONS.map((y) => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">Level</label>
            <select className="form-input" value={levelFilter} onChange={(e) => { setLevelFilter(e.target.value as any); setGradeFilter("all"); }}>
              <option value="all">All Levels</option>
              <option value="preschool">Pre-School</option>
              <option value="primary">Primary</option>
              <option value="secondary">Secondary</option>
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">Enrollment Status</label>
            <select className="form-input" value={enrollmentFilter} onChange={(e) => setEnrollmentFilter(e.target.value as any)}>
              <option value="all">All</option>
              <option value="active">Active</option>
              <option value="dropped_out">Dropped Out</option>
              <option value="graduated">Graduated</option>
              <option value="transferred">Transferred</option>
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">Shift</label>
            <select className="form-input" value={shiftFilter} onChange={(e) => setShiftFilter(e.target.value as any)}>
              <option value="all">All Shifts</option>
              <option value="morning">Morning</option>
              <option value="afternoon">Afternoon</option>
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">Grade</label>
            <select className="form-input" value={gradeFilter} onChange={(e) => setGradeFilter(e.target.value)}>
              <option value="all">All Grades</option>
              {gradeOptions.map((g) => <option key={g} value={g}>{g === "ECD" ? "ECD" : `Grade ${g}`}</option>)}
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">Section</label>
            <select className="form-input" value={sectionFilter} onChange={(e) => setSectionFilter(e.target.value)}>
              <option value="all">All Sections</option>
              {sectionOptions.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div className="form-group lg:col-span-2">
            <label className="form-label">Search by Name</label>
            <div className="relative">
              <Search className="absolute left-3 top-3.5 w-5 h-5 text-gray-400" />
              <input className="form-input pl-10" type="text" placeholder="Search student name..." value={search} onChange={(e) => setSearch(e.target.value)} />
            </div>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
          {(Object.keys(STATUS_LABELS) as EnrollmentStatus[]).map((status) => (
            <div key={status} className="rounded-lg p-3 text-center" style={{ backgroundColor: `${STATUS_COLORS[status]}15` }}>
              <p className="text-2xl font-bold" style={{ color: STATUS_COLORS[status] }}>{summary[status]}</p>
              <p className="text-xs text-gray-500">{STATUS_LABELS[status]}</p>
            </div>
          ))}
        </div>

        {resolvedClass && (
          <div className="flex items-center justify-between gap-4 bg-[#F1F5F9] border border-[#CBD5E1] rounded-lg p-4 mb-5">
            <div>
              <p className="font-semibold text-[#1C3988]">{resolvedClass.name} — {activeInResolvedClass} active student{activeInResolvedClass === 1 ? "" : "s"}</p>
              <p className="text-xs text-gray-500">Take attendance for exactly this filtered roster.</p>
            </div>
            <button
              className="btn-cyan"
              onClick={() => onTakeAttendance?.({
                classId: resolvedClass.id,
                level: resolvedClass.level,
                shift: resolvedClass.shift,
                schoolId: selectedSchoolId,
              })}
            >
              <ClipboardCheck className="w-5 h-5" /> Take Attendance
            </button>
          </div>
        )}

        {loading ? (
          <p className="text-gray-400 italic py-4">Loading students...</p>
        ) : filteredStudents.length === 0 ? (
          <p className="text-gray-400 italic py-4">No students match the current filters.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b-2 border-gray-200 text-left text-[#1C3988]">
                  <th className="py-2 px-2">Name</th>
                  <th className="py-2 px-2">Gender</th>
                  <th className="py-2 px-2">Level</th>
                  <th className="py-2 px-2">Class (Grade / Section)</th>
                  <th className="py-2 px-2">Academic Year</th>
                  <th className="py-2 px-2">Enrollment</th>
                  <th className="py-2 px-2">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredStudents.map((s) => {
                  const currentClass = s.class_id ? classById[s.class_id] : null;
                  const eligibleClasses = classes.filter((c) => c.level === s.level && c.shift === s.shift);
                  const enrollmentStatus = ((s as any).enrollment_status || "active") as EnrollmentStatus;
                  const saving = savingId === s.id;

                  return (
                    <tr key={s.id} className="border-b border-gray-100 hover:bg-[#F8FAFC]">
                      <td className="py-2 px-2 font-semibold text-[#1C3988]">{s.full_name}</td>
                      <td className="py-2 px-2 capitalize">{s.gender || "-"}</td>
                      <td className="py-2 px-2 capitalize">{s.level || "-"}</td>
                      <td className="py-2 px-2">
                        <select
                          className="form-input text-xs py-1"
                          value={s.class_id || ""}
                          disabled={saving}
                          onChange={(e) => reassignClass(s.id, e.target.value)}
                        >
                          <option value="">Unassigned</option>
                          {eligibleClasses.map((c) => (
                            <option key={c.id} value={c.id}>{c.name}</option>
                          ))}
                        </select>
                        {!currentClass && s.class_id && (
                          <span className="text-xs text-amber-600 block mt-0.5">Class not found for this year</span>
                        )}
                      </td>
                      <td className="py-2 px-2">{(s as any).academic_year || "-"}</td>
                      <td className="py-2 px-2">
                        <span
                          className="px-2 py-0.5 rounded-full text-xs font-semibold text-white"
                          style={{ backgroundColor: STATUS_COLORS[enrollmentStatus] }}
                        >
                          {STATUS_LABELS[enrollmentStatus]}
                        </span>
                      </td>
                      <td className="py-2 px-2">
                        <div className="flex gap-1.5 flex-wrap">
                          {enrollmentStatus !== "active" && (
                            <button
                              className="btn-cyan btn-sm"
                              disabled={saving}
                              onClick={() => setEnrollmentStatus(s.id, "active")}
                            >
                              <UserCheck className="w-3.5 h-3.5" /> Reactivate
                            </button>
                          )}
                          {enrollmentStatus === "active" && (
                            <>
                              <button
                                className="btn-danger btn-sm"
                                disabled={saving}
                                onClick={() => setEnrollmentStatus(s.id, "dropped_out")}
                              >
                                <UserX className="w-3.5 h-3.5" /> Dropout
                              </button>
                              <button
                                className="btn-warning btn-sm"
                                disabled={saving}
                                onClick={() => setEnrollmentStatus(s.id, "transferred")}
                              >
                                Transfer
                              </button>
                              <button
                                className="btn-primary btn-sm"
                                disabled={saving}
                                onClick={() => setEnrollmentStatus(s.id, "graduated")}
                              >
                                Graduate
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
